import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BarChart3, 
  TrendingUp, 
  Settings, 
  LogOut,
  Menu,
  ChevronDown,
  ChevronRight,
  Globe,
  MapPin,
  Target,
  FileText,
  CircleGauge,
  Users
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/useAuth';
// Add this import at the top of Sidebar.jsx
// import api from '../../utils/api';
const Sidebar = () => {
  // New state for entire sidebar toggle
  const [isCollapsed, setIsCollapsed] = useState(false);
  // States for collapsible menus
  const [isOperationsOpen, setIsOperationsOpen] = useState(false);
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { logout } = useAuth(); // No more JSON.parse manually!
  // const [error, setError] = useState('');
  // const navigate = useNavigate();
  const menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard size={20} /> },
    { name: 'Executive Dashboard', path: '/execdashboard', icon: <CircleGauge size={20} /> },
  ];

  const operationsSubItems = [
    { name: 'SBP Dashboard', path: '/operations/sbp', icon: <BarChart3 size={18} /> },
    { name: 'Liquor Dashboard', path: '/operations/liquor', icon: <BarChart3 size={18} /> },
    {name: 'Land Rates Dashboard', path: '/operations/land', icon: <BarChart3 size={18} />},
    {name: 'Manual Revenue', path: '/operations/ManualRev', icon: <BarChart3 size={18} />},
    {name: 'Ledger Download', path: '/operations/LedgerDownload', icon: <FileText size={18} /> },
    // Future items can be added here
  ];
  const analysisSubItems = [
    { name: 'County View', path: '/analysis/county', icon: <Globe size={18} /> },
    { name: 'Sub-County View', path: '/analysis/subcounty', icon: <MapPin size={18} /> },
  ];

  const settingsSubItems = [
    { name: 'Revenue Targets', path: '/settings/targets', icon: <Target size={18} /> },
    { name: 'User Management', path: '/settings/users', icon: <Users size={18} /> },
  ];

  useEffect(() => {
  if (isCollapsed) {
    setIsOperationsOpen(false);
    setIsAnalysisOpen(false);
    setIsSettingsOpen(false);
    }
  }, [isCollapsed]);
  
  return (
    <aside 
      className={`
        ${isCollapsed ? 'w-20' : 'w-64'} 
        bg-slate-900 text-slate-300 flex flex-col h-screen sticky top-0 transition-all duration-300 ease-in-out
      `}
    >
      {/* HEADER SECTION with Toggle Button */}
      <div className="p-6 border-b border-slate-800 flex items-center justify-between">
        {!isCollapsed && (
          <span className="text-sm font-semibold tracking-widest text-slate-500 uppercase animate-fadeIn">
            Menu Bar
          </span>
        )}
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1 hover:bg-slate-800 rounded-md text-slate-400 hover:text-white transition-colors"
        >
          <Menu size={20} />
        </button>
      </div>
      
      <nav className="flex-1 px-4 py-6 space-y-1">
        {/* Standard Menu Items */}
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            title={isCollapsed ? item.name : ""} // Show tooltip on hover when collapsed
            className={({ isActive }) => `
              flex items-center ${isCollapsed ? 'justify-center px-0' : 'px-4'} py-3 rounded-lg ...
            `}
          >
            <span className={`${isCollapsed ? 'mr-0' : 'mr-3'}`}>{item.icon}</span>
            {!isCollapsed && <span className="font-medium animate-fadeIn">{item.name}</span>}
          </NavLink>
        ))}
        {/* Collapsible Operations Menu */}
        <div className="space-y-1">
          <button
            onClick={() => !isCollapsed && setIsOperationsOpen(!isOperationsOpen)}
            disabled={isCollapsed} // Disable click when collapsed
            className={`
              w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'justify-between px-4'} py-3 rounded-lg ...
            `}
          >
            <div className="flex items-center">
              <Menu className={`${isCollapsed ? 'mr-0' : 'mr-3'}`} size={20} />
              {!isCollapsed && <span className="font-medium animate-fadeIn">Operations</span>}
            </div>
            {!isCollapsed && (isOperationsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
          </button>

          {!isCollapsed && isOperationsOpen && (
            <div className="pl-10 space-y-1 animate-fadeIn">
              {operationsSubItems.map((subItem) => (
                <NavLink
                  key={subItem.path}
                  to={subItem.path}
                  className={({ isActive }) => `
                    flex items-center px-4 py-2 rounded-lg text-sm transition-all duration-200
                    ${isActive 
                      ? 'text-blue-400 font-semibold' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}
                  `}
                >
                  <span className="mr-3">{subItem.icon}</span>
                  <span>{subItem.name}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {/* Collapsible Revenue Analysis Menu */}
        <div className="space-y-1">
          <button
            onClick={() => !isCollapsed && setIsAnalysisOpen(!isAnalysisOpen)}
            disabled={isCollapsed} // Disable click when collapsed
            className={`
              w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'justify-between px-4'} py-3 rounded-lg transition-all duration-200
              hover:bg-slate-800 hover:text-white
              ${isAnalysisOpen ? 'text-white' : ''}
            `}
          >
            <div className="flex items-center">
              <BarChart3 className={`${isCollapsed ? 'mr-0' : 'mr-3'}`} size={20} />
              {!isCollapsed && <span className="font-medium">Revenue Analysis</span>}
            </div>
            {!isCollapsed && (isAnalysisOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
          </button>

          {!isCollapsed && isAnalysisOpen && (
            <div className="pl-10 space-y-1 animate-fadeIn">
              {analysisSubItems.map((subItem) => (
                <NavLink
                  key={subItem.path}
                  to={subItem.path}
                  className={({ isActive }) => `
                    flex items-center px-4 py-2 rounded-lg text-sm transition-all duration-200
                    ${isActive 
                      ? 'text-blue-400 font-semibold' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}
                  `}
                >
                  <span className="mr-3">{subItem.icon}</span>
                  <span>{subItem.name}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {/* Collapsible Settings Menu */}
        <div className="space-y-1">
          <button
            onClick={() =>!isCollapsed && setIsSettingsOpen(!isSettingsOpen)}
            disabled={isCollapsed} // Disable click when collapsed
            className={`
              w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'justify-between px-4'} py-3 rounded-lg transition-all duration-200
              hover:bg-slate-800 hover:text-white
              ${isSettingsOpen ? 'text-white' : ''}
            `}
          >
            <div className="flex items-center">
              <Settings className={`${isCollapsed ? 'mr-0' : 'mr-3'}`} size={20} />
              {!isCollapsed && <span className="font-medium">Settings</span>}
            </div>
            {!isCollapsed && (isSettingsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
          </button>

          {!isCollapsed && isSettingsOpen && (
            <div className="pl-10 space-y-1 animate-fadeIn">
              {settingsSubItems.map((subItem) => (
                <NavLink
                  key={subItem.path}
                  to={subItem.path}
                  className={({ isActive }) => `
                    flex items-center px-4 py-2 rounded-lg text-sm transition-all duration-200
                    ${isActive 
                      ? 'text-blue-400 font-semibold' 
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}
                  `}
                >
                  <span className="mr-3">{subItem.icon}</span>
                  <span>{subItem.name}</span>
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>

      <div className="p-4 border-t border-slate-800">
        <button className="flex items-center w-full px-4 py-3 text-slate-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-colors"
        onClick={logout} >
        <LogOut className={`${isCollapsed ? 'mr-0' : 'mr-3'}`} size={20} />
        {!isCollapsed && <span className="font-medium animate-fadeIn">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;